import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import LegalPage from "@/components/legal/LegalPage";
import { useAuth, formatAuthError } from "@/context/AuthContext";

// Style constants — mirrored verbatim from TermsOfService / PrivacyPolicy /
// RefundPolicy / SubscriptionTerms / MyAccount so this page renders
// identically inside the shared LegalPage shell. No new CSS, no new
// components, no layout tweaks.
const H2 = { color: "#f7f7f7", fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 10, letterSpacing: "0.2px" };
const P  = { margin: "0 0 12px 0" };
const P0 = { margin: "0 0 6px 0" };
const A  = { color: "#C9A86A", textDecoration: "underline" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _fmtRemaining(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - Date.now();
  if (diff <= 0) return { expired: true, text: null };
  const days  = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (days >= 1) return { expired: false, text: `${days} day${days === 1 ? "" : "s"} ${hours} hour${hours === 1 ? "" : "s"} remaining` };
  return { expired: false, text: `${hours} hour${hours === 1 ? "" : "s"} remaining` };
}

export default function PlanPricing() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const tier = user?.tier || "trial";
  const isTrial   = tier === "trial";
  const hasBasic  = !!user?.basic_owned;
  const remaining = isTrial ? _fmtRemaining(user?.trial_expires_at) : null;

  const buyBasic = async () => {
    const email = user?.email;
    if (!email) { toast.error("Sign in first."); return; }
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const r = await axios.post(`${API}/stripe/basic-checkout`, { email, origin_url: origin });
      const url = r.data?.checkout_url;
      if (!url) throw new Error("No checkout URL returned.");
      window.location.href = url;
    } catch (err) {
      toast.error(formatAuthError(err));
      setBusy(false);
    }
  };

  return (
    <LegalPage title="Plan & Pricing" testid="account-plan-pricing">
      <h2 style={H2}>Trial Plan</h2>
      <p style={P0}>Your free trial lasts 72 hours (3 days).</p>
      <p style={P0}>You can create one collection per type (Wine, Whisky, Spirits).</p>
      <p style={P0}>You can add up to 12 items during the trial period.</p>
      <p style={P}>All other Basic features are available except unlimited items and unlimited collections.</p>

      <h2 style={H2}>Basic Plan</h2>
      <p style={P0}>One-off price: $35 NZD</p>
      <p style={P0}>Unlimited collections</p>
      <p style={P0}>Unlimited items</p>
      <p style={P0}>Up to 3 photos per item</p>
      <p style={P0}>Unlimited categories</p>
      <p style={P}>Manual entry</p>
      {hasBasic ? (
        <p style={P} data-testid="account-plan-pricing-basic-owned">You already own Basic.</p>
      ) : (
        <p style={P}>
          <button
            type="button"
            onClick={buyBasic}
            disabled={busy}
            data-testid="account-plan-pricing-buy-basic"
            style={{
              background: "#C9A86A",
              color: "#0f0f0f",
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "0.5px",
              padding: "10px 18px",
              borderRadius: 6,
              border: "none",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >{busy ? "Redirecting…" : "Purchase Basic"}</button>
        </p>
      )}

      <h2 style={H2}>Premium Plan</h2>
      <p style={P}>Premium coming soon</p>

      <h2 style={H2}>Legal Information</h2>
      <p style={P0}><Link to="/legal/subscription" style={A} data-testid="account-plan-pricing-subscription-link">Subscription Terms</Link></p>
      <p style={P}><Link to="/legal/refund" style={A} data-testid="account-plan-pricing-refund-link">Refund Policy</Link></p>

      <h2 style={H2}>Notes</h2>
      <p style={P}>This page provides an overview of available plans and upgrade options. You can manage your account details from the My Account page.</p>
    </LegalPage>
  );
}
